import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';

import type { OfficePreviewKind } from './artifact-display.js';
import { registerOfficeFonts } from './office-fonts.js';

/** Options every viewer gets: no web fonts from a CDN, and no links a crafted file could navigate with. */
const SAFE_VIEWER_OPTIONS = { useGoogleFonts: false, enableHyperlinks: false };

/**
 * A page- or slide-at-a-time viewer, as `DocxViewer` and `PptxViewer` both are. `fit` fills the width for a
 * page, which scrolls, and fits the whole slide into the box, which must never be cut.
 */
interface PagedViewer {
  load(bytes: ArrayBuffer): Promise<void>;
  destroy(): void;
  fit(): Promise<void>;
  goTo(index: number): Promise<void>;
  position(): PagePosition;
}

type ViewStatus = 'loading' | 'ready' | 'error' | 'too-large';

/**
 * The renderer refuses files past its resource limits — a sheet over 250,000 cells, an image too large to
 * decode, a package that inflates past its budget. That is "too large", not "broken", and is said so.
 */
function isOverRendererLimit(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return (
    code === 'ooxml-resource-limit' || code === 'ooxml-decoded-image-limit'
  );
}

/** Which page is on screen, how many there are, and whether more may still be laid out. */
export interface PagePosition {
  current: number;
  total: number;
  complete: boolean;
}

/**
 * One load of one file. `cancelled` flips when the url or kind changes or the view unmounts; `release`
 * destroys whichever viewer was built, at most once, whether the load finished, failed or was abandoned.
 */
interface LoadSession {
  cancelled: boolean;
  /** Called as the viewer turns pages or, for a long document laid out in the background, finds more. */
  onPosition: (position: PagePosition) => void;
  destroyViewer?: () => void;
  release(): void;
}

/** The elements the viewer draws into, and the hook's view of the result. */
interface OfficeViewer extends ViewerElements {
  status: ViewStatus;
  position: PagePosition;
  turn: (step: number) => Promise<void>;
  /** Width over height of the slide on screen, so a deck's box can take the slide's own shape. */
  slideAspect: number;
}

/** The slide shape assumed until a deck is drawn: the 16:9 that PowerPoint defaults to. */
const DEFAULT_SLIDE_ASPECT = 16 / 9;

/**
 * The shape of the slide a deck drew, read off the fitted canvas — `fitPage` scales uniformly, so the
 * canvas keeps the slide's proportions whatever box it fitted. When the shape changes, the box it sizes
 * changes height only, which the width-only refit does not see, so the slide is fitted again here.
 */
function useSlideAspect(
  kind: OfficePreviewKind,
  canvasRef: RefObject<HTMLCanvasElement | null>,
  paged: PagedViewer | null,
): number {
  const [slideAspect, setSlideAspect] = useState(DEFAULT_SLIDE_ASPECT);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (kind !== 'pptx' || !paged || !canvas?.width || !canvas.height) return;
    setSlideAspect(canvas.width / canvas.height);
  }, [kind, canvasRef, paged]);
  useEffect(() => {
    if (kind === 'pptx' && paged) void paged.fit();
  }, [kind, paged, slideAspect]);
  return slideAspect;
}

/**
 * Build the viewer for a Word or PowerPoint file on the canvas, without loading anything yet. The library
 * moves the canvas into a wrapper of its own on construction, so the caller must `destroy()` the viewer to
 * hand the canvas back.
 */
async function createPaged(
  kind: 'docx' | 'pptx',
  canvas: HTMLCanvasElement,
  container: HTMLElement,
  onPosition: (position: PagePosition) => void,
): Promise<PagedViewer> {
  if (kind === 'docx') {
    const { DocxViewer } = await import('@silurus/ooxml/docx');
    const viewer = new DocxViewer(canvas, {
      ...SAFE_VIEWER_OPTIONS,
      container,
      progressiveLayout: true,
      onPageChange: (index, total, complete) =>
        onPosition({ current: index, total, complete }),
    });
    return {
      load: (bytes) => viewer.load(bytes),
      destroy: () => viewer.destroy(),
      fit: () => viewer.fitWidth(),
      goTo: (index) => viewer.goToPage(index),
      position: () => ({
        current: viewer.currentPage,
        total: viewer.pageCount,
        complete: viewer.layoutComplete,
      }),
    };
  }
  const { PptxViewer } = await import('@silurus/ooxml/pptx');
  const viewer = new PptxViewer(canvas, {
    ...SAFE_VIEWER_OPTIONS,
    onSlideChange: (index, total) =>
      onPosition({ current: index, total, complete: true }),
  });
  return {
    load: (bytes) => viewer.load(bytes),
    destroy: () => viewer.destroy(),
    fit: () => viewer.fitPage(),
    goTo: (index) => viewer.goToSlide(index),
    position: () => ({
      current: viewer.slideIndex,
      total: viewer.slideCount,
      complete: true,
    }),
  };
}

/** A fresh session whose `release` destroys the viewer it was handed, once. */
function openSession(
  onPosition: (position: PagePosition) => void,
): LoadSession {
  const session: LoadSession = {
    cancelled: false,
    onPosition,
    release() {
      session.destroyViewer?.();
      session.destroyViewer = undefined;
    },
  };
  return session;
}

/** Hand a workbook to the sheet viewer, which builds its own canvas and tabs inside the host. */
async function loadWorkbook(
  host: HTMLElement,
  bytes: ArrayBuffer,
  session: LoadSession,
): Promise<void> {
  const { XlsxViewer } = await import('@silurus/ooxml/xlsx');
  if (session.cancelled) return;
  const viewer = new XlsxViewer(host, SAFE_VIEWER_OPTIONS);
  session.destroyViewer = () => viewer.destroy();
  await viewer.load(bytes);
}

/**
 * Draw a Word or PowerPoint file on the canvas and fit it to the page box. Answers the viewer once it is
 * drawn, or null when the view went away first — a viewer built after that is destroyed straight away.
 */
async function loadPaged(
  kind: 'docx' | 'pptx',
  elements: { canvas: HTMLCanvasElement; pageBox: HTMLElement },
  bytes: ArrayBuffer,
  session: LoadSession,
): Promise<PagedViewer | null> {
  const viewer = await createPaged(
    kind,
    elements.canvas,
    elements.pageBox,
    (position) => {
      if (!session.cancelled) session.onPosition(position);
    },
  );
  session.destroyViewer = () => viewer.destroy();
  if (session.cancelled) {
    session.release();
    return null;
  }
  await viewer.load(bytes);
  if (session.cancelled) return null;
  await viewer.fit();
  return viewer;
}

/**
 * Re-fit the page whenever its unpadded box changes WIDTH — entering full screen is the case that matters.
 * Height is ignored: a Word page's box grows with the canvas `fitWidth` draws, so refitting on height
 * would feed the fit back into itself.
 */
function useRefitOnWidthChange(
  pageBoxRef: RefObject<HTMLElement | null>,
  viewer: PagedViewer | null,
): void {
  useEffect(() => {
    const pageBox = pageBoxRef.current;
    if (!pageBox || !viewer || typeof ResizeObserver === 'undefined') return;
    let lastWidth: number | null = null;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? null;
      if (width === lastWidth) return;
      lastWidth = width;
      void viewer.fit();
    });
    observer.observe(pageBox);
    return () => observer.disconnect();
  }, [pageBoxRef, viewer]);
}

/** The three elements the viewer draws into. */
interface ViewerElements {
  hostRef: RefObject<HTMLDivElement | null>;
  pageBoxRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
}

/** What one load drew: the paged viewer for a Word or PowerPoint file, null for a workbook. */
type DrawnFile = { paged: PagedViewer | null };

/**
 * Fetch the bytes and the fonts together, then draw with the viewer for this kind. Answers null when the
 * view went away before anything was drawn.
 */
async function drawFile(
  url: string,
  kind: OfficePreviewKind,
  elements: ViewerElements,
  session: LoadSession,
): Promise<DrawnFile | null> {
  const [bytes] = await Promise.all([
    fetch(url).then((response) => response.arrayBuffer()),
    registerOfficeFonts(),
  ]);
  const host = elements.hostRef.current;
  if (session.cancelled || !host) return null;
  if (kind === 'xlsx') {
    await loadWorkbook(host, bytes, session);
    return { paged: null };
  }
  const canvas = elements.canvasRef.current;
  const pageBox = elements.pageBoxRef.current;
  if (!canvas || !pageBox) return null;
  const paged = await loadPaged(kind, { canvas, pageBox }, bytes, session);
  return paged ? { paged } : null;
}

/** The position before anything is drawn: no pages, so no page bar. */
const NO_PAGES: PagePosition = { current: 0, total: 0, complete: true };

/**
 * Draw `url` whenever it or the kind changes. Every viewer is destroyed when the url or kind changes, on
 * unmount, when cancelled mid-load, and as soon as its load fails.
 */
function useDrawnFile(
  url: string,
  kind: OfficePreviewKind,
  elements: ViewerElements,
): {
  status: ViewStatus;
  paged: PagedViewer | null;
  position: PagePosition;
  setPosition: (position: PagePosition) => void;
} {
  const [status, setStatus] = useState<ViewStatus>('loading');
  const [paged, setPaged] = useState<PagedViewer | null>(null);
  const [position, setPosition] = useState<PagePosition>(NO_PAGES);
  useEffect(() => {
    const session = openSession(setPosition);
    setStatus('loading');
    setPaged(null);
    setPosition(NO_PAGES);
    drawFile(url, kind, elements, session)
      .then((drawn) => {
        if (session.cancelled || !drawn) return;
        setPaged(drawn.paged);
        if (drawn.paged) setPosition(drawn.paged.position());
        setStatus('ready');
      })
      .catch((error: unknown) => {
        session.release();
        if (session.cancelled) return;
        setStatus(isOverRendererLimit(error) ? 'too-large' : 'error');
      });
    return () => {
      session.cancelled = true;
      session.release();
    };
  }, [url, kind, elements]);
  return { status, paged, position, setPosition };
}

/**
 * Load `url` into the `@silurus/ooxml` viewer for its kind, keep the page fitted to its box, and turn
 * pages. The caller renders the elements the returned refs point at.
 */
export function useOfficeViewer(
  url: string,
  kind: OfficePreviewKind,
): OfficeViewer {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const pageBoxRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const elements = useMemo(() => ({ hostRef, pageBoxRef, canvasRef }), []);
  const { status, paged, position, setPosition } = useDrawnFile(
    url,
    kind,
    elements,
  );
  useRefitOnWidthChange(pageBoxRef, paged);
  const slideAspect = useSlideAspect(kind, canvasRef, paged);

  /** Step from the page the viewer is on now, not the last one rendered, so a fast double click counts twice. */
  const turn = async (step: number): Promise<void> => {
    if (!paged) return;
    await paged.goTo(paged.position().current + step);
    setPosition(paged.position());
    if (hostRef.current) {
      hostRef.current.scrollTop = 0;
      hostRef.current.scrollLeft = 0;
    }
  };

  return { ...elements, status, position, turn, slideAspect };
}
